/**
 * Continuous seat-clock for the workshop scarcity-display layer.
 *
 * The seat count is a deterministic function of (workshop date, now, and the
 * series' `curve` + `dayBefore` ramp):
 *   • Before the day-before window, the count follows an authorable CURVE —
 *     a list of `{daysOut, seats}` control points, linearly interpolated by
 *     days-to-workshop (flat at the outermost point beyond it). This lets us
 *     hit specific targets like "11 seats two days out, 9 the day before"
 *     instead of a single straight slope.
 *   • On the day before, a wall-clock RAMP takes over: `startSeats` at
 *     midnight ET decaying linearly to `eveningSeats` by `eveningHourET`,
 *     then held at `eveningSeats` through workshop start. This produces the
 *     "starts at 9, counts down to 3 by evening" behavior, anchored to the
 *     ET clock rather than to hours-until-start.
 *   • A display ceiling of 80% of `startingCount` is applied on top — the
 *     full count never renders, so the meter can never read "wide open"
 *     (see `displayCeilingFor`).
 *
 * `startingCount` is now only the METER SCALE (the denominator for the bar
 * fill level) and the SSR-neutral count — NOT the displayed number, which
 * comes from `curve` / `dayBefore`.
 *
 * Two override states wrap the count:
 *   • `last-call` — from midnight ET the day before through workshop start.
 *     Keeps the numeric count (the ramp value); callers label it "Last call".
 *   • `sold-out` — when the caller passes `isSoldOut=true` (driven by
 *     `event.status === 'sold_out'`). Overrides everything else.
 *
 * Pure `computeClockState` is exported for testability. `useSeatClock` and
 * `useSeatClocks` (multi-session variant) wrap it with a 10-minute interval
 * driver. SSR-safe via post-mount state — server emits a neutral "full"
 * result, the client takes over on hydration to avoid React 18 mismatches.
 *
 * Plan: docs/plans/2026-05-26-001-feat-workshop-scarcity-display-plan.md (U2)
 */

import { useEffect, useState } from "react";

export type ClockState = "clock" | "last-call" | "sold-out";

/**
 * 1-4 maps the seatsLeft / startingCount ratio onto the design's 4-bar meter:
 *   3 = >50%   (3 green + 1 gray — the maximum ever rendered)
 *   2 = 25-50% (2 green + 2 gray)
 *   1 = ≤25%   (1 red + 3 gray, "tight")
 *
 * Level 4 (full bars) is intentionally unreachable: a maxed meter reads as
 * zero scarcity. The type keeps `4` so legacy values can't break consumers,
 * but no code path produces it anymore (2026-06-12).
 */
export type FillLevel = 1 | 2 | 3 | 4;

/**
 * The highest seat count we ever DISPLAY: 80% of startingCount. The full
 * startingCount never renders, so "35 of 35 open" is impossible by
 * construction regardless of how far out the session is.
 */
export function displayCeilingFor(startingCount: number): number {
  return Math.max(1, Math.round(startingCount * 0.8));
}

export type ClockResult = {
  state: ClockState;
  /** Number of seats displayed in the meter. null only when state is sold-out. */
  seatsLeft: number | null;
  /** Visual bar fill level — 1 (tight, red) through 4 (full, all green). */
  fillLevel: FillLevel;
  /**
   * "Filling" / medium-depletion flag. Set by the monotonic-depletion pass
   * (`applyMonotonicDepletion`) on a NEARER upcoming date that has been clamped
   * to look less open than a LATER one. Consumers render this as the third
   * meter color (gold/amber) — between forest (plenty) and terra (last-call).
   * Independent of `fillLevel`: a stop can be `medium` at any fill level.
   */
  medium?: boolean;
};

/** A control point on the decay curve: `seats` shown when `daysOut` to the
 *  workshop. The curve interpolates linearly between adjacent points. */
export type SeatCurvePoint = { daysOut: number; seats: number };

/** Wall-clock ramp for the day before the workshop. */
export type DayBeforeRamp = {
  /** Seats shown at midnight ET on the day before (window open). */
  startSeats: number;
  /** Seats shown from the evening cutoff through workshop start (the floor). */
  eveningSeats: number;
  /** ET hour (0-23) at which the count reaches `eveningSeats`. */
  eveningHourET: number;
};


const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Linearly interpolate the displayed seat count from the curve for a given
 * days-to-workshop. `curve` must be sorted DESCENDING by `daysOut`. Beyond the
 * outermost point the count is flat at that point's seats; inside the innermost
 * point (handled by the day-before ramp in practice) it's flat at the last.
 */
function seatsFromCurve(daysToWorkshop: number, curve: SeatCurvePoint[]): number {
  if (curve.length === 0) return 1;
  if (daysToWorkshop >= curve[0].daysOut) return curve[0].seats;
  const last = curve[curve.length - 1];
  if (daysToWorkshop <= last.daysOut) return last.seats;
  for (let i = 0; i < curve.length - 1; i++) {
    const hi = curve[i];
    const lo = curve[i + 1];
    if (daysToWorkshop <= hi.daysOut && daysToWorkshop >= lo.daysOut) {
      const t = (daysToWorkshop - lo.daysOut) / (hi.daysOut - lo.daysOut);
      return Math.max(1, Math.round(lo.seats + t * (hi.seats - lo.seats)));
    }
  }
  return last.seats;
}

/**
 * The day-before wall-clock ramp: `startSeats` at midnight ET decaying linearly
 * to `eveningSeats` at the evening cutoff, then held at `eveningSeats`.
 */
function dayBeforeRampSeats(
  nowMs: number,
  midnightMs: number,
  eveningMs: number,
  startSeats: number,
  eveningSeats: number,
): number {
  if (eveningMs <= midnightMs || nowMs >= eveningMs) return eveningSeats;
  if (nowMs <= midnightMs) return startSeats;
  const t = (nowMs - midnightMs) / (eveningMs - midnightMs);
  return Math.max(1, Math.round(startSeats + t * (eveningSeats - startSeats)));
}

/**
 * A specific ET hour on the calendar day BEFORE the workshop, as a Date.
 * `hourET=0` is midnight ET (when the Last Call window opens); `hourET=20` is
 * 8:00 PM ET (a typical evening cutoff for the day-before ramp).
 */
function dayBeforeWorkshopET(workshopStart: Date, hourET: number): Date {
  // Extract workshop's calendar day in ET.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(workshopStart);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  const day = Number(parts.find((p) => p.type === "day")?.value);

  // Probe ET UTC offset on the day-before at noon (away from DST switchovers).
  const probeUtc = Date.UTC(year, month - 1, day - 1, 12, 0, 0);
  const probeParts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date(probeUtc));
  const etHourAtProbe = Number(probeParts.find((p) => p.type === "hour")?.value);
  const offsetHours = etHourAtProbe - 12; // typically -4 (EDT) or -5 (EST)
  // `hourET` ET on the day-before = UTC time such that ET clock reads hourET.
  // Date.UTC normalizes hour overflow (e.g. 20 - (-4) = 24 → next UTC day 00:00).
  return new Date(Date.UTC(year, month - 1, day - 1, hourET - offsetHours, 0, 0));
}

/** Map seats-left ratio to the bar fill level. Caps at 3 — full bars never render. */
function ratioToFillLevel(seatsLeft: number, startingCount: number): FillLevel {
  if (startingCount <= 0) return 1;
  const ratio = seatsLeft / startingCount;
  if (ratio > 0.50) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

/**
 * Pure clock-state computation. Exported for testability.
 *
 * Inputs:
 *   - `now`: current time
 *   - `workshopStart`: the session's startDatetime
 *   - `startingCount`: METER SCALE only — denominator for the bar fill level and
 *     the SSR-neutral count. NOT the displayed seat number.
 *   - `curve`: `{daysOut, seats}` control points (sorted DESC by daysOut) that
 *     define the displayed count before the day-before window.
 *   - `dayBefore`: wall-clock ramp for the final day (9 → 3 by evening, etc.)
 *   - `isSoldOut`: when true, returns sold-out state regardless of time
 *   - `maxCapacity`: optional render-time cap — seatsLeft is capped at this value if smaller.
 */
export function computeClockState({
  now,
  workshopStart,
  startingCount,
  curve,
  dayBefore,
  isSoldOut = false,
  maxCapacity = null,
}: {
  now: Date;
  workshopStart: Date | string;
  startingCount: number;
  curve: SeatCurvePoint[];
  dayBefore: DayBeforeRamp;
  isSoldOut?: boolean;
  maxCapacity?: number | null;
}): ClockResult {
  if (isSoldOut) {
    return { state: "sold-out", seatsLeft: null, fillLevel: 1 };
  }

  const start = workshopStart instanceof Date ? workshopStart : new Date(workshopStart);
  if (Number.isNaN(start.getTime()) || startingCount < 1) {
    // Defensive: malformed inputs fall through to a neutral wide-open state so
    // the UI doesn't break. Caller can choose to hide it.
    return { state: "clock", seatsLeft: displayCeilingFor(startingCount), fillLevel: 3 };
  }

  const nowMs = now.getTime();
  const startMs = start.getTime();

  // Workshop has already started — treat as terminal; render neutral wide-open.
  // Callers usually filter past sessions out of "upcoming" before reaching here.
  if (nowMs >= startMs) {
    return { state: "clock", seatsLeft: displayCeilingFor(startingCount), fillLevel: 3 };
  }

  // The day-before window opens at midnight ET the calendar day before the
  // workshop. Inside it, a wall-clock ramp drives the count and we label it
  // "last-call"; outside it, the curve drives the count by days-to-workshop.
  const midnightBeforeMs = dayBeforeWorkshopET(start, 0).getTime();
  let seatsLeft: number;
  let state: ClockState;
  if (nowMs >= midnightBeforeMs) {
    const eveningMs = dayBeforeWorkshopET(start, dayBefore.eveningHourET).getTime();
    seatsLeft = dayBeforeRampSeats(
      nowMs,
      midnightBeforeMs,
      eveningMs,
      dayBefore.startSeats,
      dayBefore.eveningSeats,
    );
    state = "last-call";
  } else {
    const daysToWorkshop = (startMs - nowMs) / MS_PER_DAY;
    seatsLeft = seatsFromCurve(daysToWorkshop, curve);
    state = "clock";
  }

  // Display ceiling: the meter never renders the full scale — a max-count
  // display reads as zero scarcity.
  seatsLeft = Math.min(seatsLeft, displayCeilingFor(startingCount));

  // Runtime cap: never display more "seats left" than the real cohort actually
  // holds. Guards against config drift where the curve exceeds maxCapacity.
  if (maxCapacity != null && seatsLeft > maxCapacity) {
    seatsLeft = Math.max(1, maxCapacity);
  }

  seatsLeft = Math.max(1, seatsLeft);
  const fillLevel = ratioToFillLevel(seatsLeft, startingCount);
  return { state, seatsLeft, fillLevel };
}

/**
 * React hook wrapping `computeClockState` with a 10-min re-render driver.
 *
 * SSR/hydration: returns a neutral "full" result until the component mounts.
 * Avoids React 18 hydration warnings on first paint by emitting identical
 * markup on server and pre-mount client.
 */
export function useSeatClock({
  workshopStart,
  startingCount,
  curve,
  dayBefore,
  isSoldOut = false,
  maxCapacity = null,
}: {
  workshopStart: Date | string;
  startingCount: number;
  curve: SeatCurvePoint[];
  dayBefore: DayBeforeRamp;
  isSoldOut?: boolean;
  maxCapacity?: number | null;
}): ClockResult {
  const [mounted, setMounted] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setMounted(true);
    const id = setInterval(() => setTick((t) => t + 1), 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!mounted) {
    return { state: "clock", seatsLeft: displayCeilingFor(startingCount), fillLevel: 3 };
  }

  void tick;
  return computeClockState({
    now: new Date(),
    workshopStart,
    startingCount,
    curve,
    dayBefore,
    isSoldOut,
    maxCapacity,
  });
}

/**
 * Enforce MONOTONIC depletion across an ordered (soonest-first) list of upcoming
 * clock results: a CLOSER date must never look more open than a LATER one.
 *
 * The time-driven curve alone can tie two dates that are both past the curve's
 * outermost control point (e.g. two sessions a week-plus out both read "15 seats
 * open"), which makes the schedule look implausible — the soonest seat should
 * always be the scarcest. This pass layers a proximity constraint on top of the
 * existing time decay WITHOUT mutating the underlying curve math:
 *
 *   1. Rank cap — the soonest bookable date (rank 0) is capped to look like
 *      ~"a couple days out" (≤ ~45% of the meter scale) so it can't sit wide
 *      open while later dates exist. Each later rank relaxes the cap.
 *   2. Monotonic clamp — walking from soonest → latest, each later date's
 *      seat count is forced to be ≥ the previous one's, so openness only ever
 *      increases with distance. fillLevel is re-derived from the clamped count.
 *   3. Medium flag — any rank-capped date that lands in the "filling" band
 *      (>25% and ≤50% of scale, i.e. fillLevel 2) is marked `medium` so the
 *      meter renders gold/amber rather than forest.
 *
 * Sold-out and last-call results pass through untouched (they already convey
 * maximal scarcity, and last-call is the soonest-date terminal state). Inputs
 * are returned new; the array is not mutated in place.
 */
export function applyMonotonicDepletion(
  ordered: ClockResult[],
  startingCount: number,
): ClockResult[] {
  if (ordered.length <= 1 || startingCount < 1) return ordered;

  // Per-rank openness ceiling as a fraction of the meter scale. Rank 0 (soonest)
  // is held in the "filling" band; later ranks relax toward the display ceiling.
  // Tuned so a Cowork pair a week+ out reads ~rank0 ≈ 11/24 (gold, ~3 of 6 bars)
  // and rank1 ≈ 15/24 (forest, ~5 of 6 bars).
  const ceiling = displayCeilingFor(startingCount);
  const rankFraction = (rank: number): number => {
    // 0 → 0.46, 1 → 0.66, 2 → 0.78, then asymptote toward the display ceiling.
    return Math.min(0.8, 0.46 + rank * 0.2);
  };

  // First pass: rank-cap each (skip terminal states).
  let prevSeats = 0;
  return ordered.map((clock, rank) => {
    if (clock.state === "sold-out" || clock.seatsLeft == null) {
      return clock;
    }
    // Last-call (soonest, day-before) is already maximally scarce — leave it,
    // but still record its seat count as the floor for later dates.
    if (clock.state === "last-call") {
      prevSeats = clock.seatsLeft;
      return clock;
    }

    const rankCap = Math.max(1, Math.round(ceiling * rankFraction(rank)));
    // Cap by rank, then clamp to be no more open than the previous (nearer) date.
    let seats = Math.min(clock.seatsLeft, rankCap);
    if (seats < prevSeats) seats = prevSeats;
    seats = Math.min(seats, ceiling);
    seats = Math.max(1, seats);
    prevSeats = seats;

    const fillLevel = ratioToFillLevel(seats, startingCount);
    // "Filling" band (fillLevel 2) on a rank-capped date → gold/amber.
    const medium = fillLevel === 2;
    return { ...clock, seatsLeft: seats, fillLevel, medium };
  });
}

/**
 * Multi-session variant. Drives the 10-min re-render at the page level so two
 * surfaces (hero + ticketing strip) share a single timer instead of mounting
 * one per tile. Returns a record keyed by session id.
 */
export function useSeatClocks<T extends { id: number; startDatetime: Date | string; status?: string; maxCapacity?: number | null }>(
  sessions: T[],
  config: { startingCount: number; curve: SeatCurvePoint[]; dayBefore: DayBeforeRamp } | null,
): Record<number, ClockResult> {
  const [mounted, setMounted] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setMounted(true);
    const id = setInterval(() => setTick((t) => t + 1), 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const result: Record<number, ClockResult> = {};

  // Pre-mount or no config: every session renders neutral wide-open (3 bars —
  // full bars never render, even during the SSR/pre-mount frame).
  if (!mounted || config == null) {
    for (const s of sessions) {
      result[s.id] = {
        state: "clock",
        seatsLeft: config != null ? displayCeilingFor(config.startingCount) : 0,
        fillLevel: 3,
      };
    }
    return result;
  }

  void tick;
  const now = new Date();
  // Order soonest-first so the monotonic-depletion pass can enforce "a closer
  // date is never more open than a later one". Callers usually pass an
  // already-ascending list, but sort defensively so the constraint holds
  // regardless of input order.
  const ordered = [...sessions].sort(
    (a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime(),
  );
  const clocks = ordered.map((s) =>
    computeClockState({
      now,
      workshopStart: s.startDatetime,
      startingCount: config.startingCount,
      curve: config.curve,
      dayBefore: config.dayBefore,
      isSoldOut: s.status === "sold_out",
      maxCapacity: s.maxCapacity ?? null,
    }),
  );
  const adjusted = applyMonotonicDepletion(clocks, config.startingCount);
  ordered.forEach((s, i) => {
    result[s.id] = adjusted[i];
  });
  return result;
}
